/**
 * Shared hook for handling Turnkey sign-in with misconfiguration handling.
 * Use this in any component that has a "Sign In" button.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { getTurnkeyMissingKeys, isTurnkeyConfigured } from '@/config/turnkeyPublicConfig';

interface UseTurnkeySignInResult {
  /** Whether Turnkey is properly configured */
  isConfigured: boolean;
  /** Whether auth is loading */
  isLoading: boolean;
  /** Whether user is connected/authenticated */
  connected: boolean;
  /** Call this to initiate sign-in (handles misconfiguration automatically) */
  handleSignIn: () => void;
  /** Missing env var keys (for display) */
  missingKeys: string[];
}

/**
 * Hook that provides a safe sign-in handler that:
 * - If configured: calls Turnkey login
 * - If misconfigured: shows toast with missing keys and navigates to /auth/diagnostics
 */
export function useTurnkeySignIn(): UseTurnkeySignInResult {
  const navigate = useNavigate();
  const { login, isLoading, connected, isConfigured: providerConfigured } = useTurnkeyWallet();
  
  // Double-check config status
  const isConfigured = isTurnkeyConfigured() && providerConfigured;
  const missingKeys = getTurnkeyMissingKeys();

  const handleSignIn = useCallback(() => {
    if (!isConfigured) {
      const keyList = missingKeys.join(', ');
      toast.error('Sign-in unavailable', {
        description: `Missing: ${keyList}. Set in Lovable Environment Variables and redeploy.`,
        duration: 8000,
      });
      navigate('/auth/diagnostics');
      return;
    }
    login();
  }, [isConfigured, missingKeys, login, navigate]);

  return {
    isConfigured,
    isLoading,
    connected,
    handleSignIn,
    missingKeys,
  };
}
